/**
 * Community Page - Cat Shows, Clubs, Registries & Partners
 * ThePedigreeApp
 */

import { useState } from 'react';
import { Calendar, MapPin, ExternalLink, Users, Award, Trophy, Mail } from 'lucide-react';
import { Card, Button, Badge } from '../components/shared/UIComponents';
import { cn } from '../utils/helpers';

const CAT_SHOWS = [
  { id: 1, name: "SACC National Championship", date: "15-16 March 2025", location: "Johannesburg Expo Centre", province: "Gauteng", organizer: "SACC", description: "The premier cat show in South Africa featuring all breeds.", registrationOpen: true },
  { id: 2, name: "Western Cape Cat Show", date: "12 April 2025", location: "Cape Town ICC", province: "Western Cape", organizer: "SACC Western Cape", description: "Annual regional show showcasing Western Cape breeders.", registrationOpen: true },
  { id: 3, name: "KZN Cat Fanciers Show", date: "10 May 2025", location: "Durban Exhibition Centre", province: "KwaZulu-Natal", organizer: "CASA", description: "Celebrating the best cats from KwaZulu-Natal.", registrationOpen: false },
  { id: 4, name: "Pretoria All-Breeds Show", date: "7 June 2025", location: "Pretoria Show Grounds", province: "Gauteng", organizer: "CFSA", description: "Open to all registered pedigree cats.", registrationOpen: false }
];

const REGISTRIES = [
  { id: 1, name: "SACC - South African Cat Council", shortName: "SACC", description: "The largest cat registry in South Africa, affiliated with WCF (World Cat Federation).", members: "500+ breeders", founded: "1947", website: "https://sacc.org.za", email: "info@sacc.org.za", affiliation: "WCF" },
  { id: 2, name: "CASA - Cat Association of Southern Africa", shortName: "CASA", description: "Independent registry promoting ethical breeding practices and cat welfare.", members: "200+ breeders", founded: "1985", website: "https://casa.org.za", email: "info@casa.org.za", affiliation: "Independent" },
  { id: 3, name: "CFSA - Cat Federation of Southern Africa", shortName: "CFSA", description: "Registry affiliated with TICA, supporting international breed standards.", members: "150+ breeders", founded: "1992", website: "https://cfsa.org.za", email: "info@cfsa.org.za", affiliation: "TICA" }
];

const CAT_CLUBS = [
  { id: 1, name: "Persian Cat Club of South Africa", breed: "Persian", members: "120+", contact: "persian@clubs.co.za" },
  { id: 2, name: "Maine Coon Society SA", breed: "Maine Coon", members: "85+", contact: "mainecoon@clubs.co.za" },
  { id: 3, name: "Siamese & Oriental Cat Club", breed: "Siamese / Oriental", members: "95+", contact: "siamese@clubs.co.za" },
  { id: 4, name: "British Shorthair Fanciers", breed: "British Shorthair", members: "60+", contact: "british@clubs.co.za" },
  { id: 5, name: "Bengal Cat Club SA", breed: "Bengal", members: "70+", contact: "bengal@clubs.co.za" },
  { id: 6, name: "Ragdoll Lovers SA", breed: "Ragdoll", members: "55+", contact: "ragdoll@clubs.co.za" },
  { id: 7, name: "Burmese Cat Club SA", breed: "Burmese", members: "45+", contact: "burmese@clubs.co.za" },
  { id: 8, name: "Abyssinian & Somali Club", breed: "Abyssinian / Somali", members: "40+", contact: "abyssinian@clubs.co.za" }
];

const PARTNERS = [
  { id: 1, name: "Royal Canin South Africa", type: "Nutrition Partner", description: "Premium cat nutrition tailored to breed, age, and health needs.", website: "https://royalcanin.co.za" },
  { id: 2, name: "Hill's Pet Nutrition", type: "Nutrition Partner", description: "Science-led nutrition for cats at every life stage.", website: "https://hillspet.co.za" },
  { id: 3, name: "Vetstore", type: "Retail Partner", description: "Online pet supplies and veterinary products.", website: "https://vetstore.co.za" }
];

export const CommunityPage = () => {
  const [activeTab, setActiveTab] = useState('shows');

  const tabs = [
    { id: 'shows', label: 'Cat Shows', icon: Trophy },
    { id: 'registries', label: 'Registries', icon: Award },
    { id: 'clubs', label: 'Breed Clubs', icon: Users },
    { id: 'partners', label: 'Partners', icon: Handshake },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-feline-600 to-feline-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="w-16 h-16 mx-auto mb-4 text-feline-200" />
          <h1 className="text-4xl font-display font-bold mb-4">Community</h1>
          <p className="text-xl text-feline-100 max-w-2xl mx-auto">
            Connect with South Africa's pedigree cat community.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={cn(
                'flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all',
                activeTab === tab.id ? 'bg-feline-500 text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              )}>
                <Icon className="w-5 h-5" /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Cat Shows */}
        {activeTab === 'shows' && (
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Upcoming Cat Shows</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {CAT_SHOWS.map((show) => (
                <Card key={show.id} className="hover:shadow-lg transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold text-gray-900">{show.name}</h3>
                    {show.registrationOpen && <Badge className="bg-paw-100 text-paw-700">Open</Badge>}
                  </div>
                  <p className="text-feline-600 font-medium mb-2">{show.organizer}</p>
                  <p className="text-gray-600 mb-4">{show.description}</p>
                  <div className="flex items-center gap-2 text-gray-600 mb-2">
                    <Calendar className="w-4 h-4 text-feline-500" /> {show.date}
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-4 h-4 text-feline-500" /> {show.location}, {show.province}
                  </div>
                </Card>
              ))}
            </div>
            <Card className="mt-8 bg-feline-50 border-feline-200">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Trophy className="w-10 h-10 text-feline-500" />
                <div className="text-center sm:text-left flex-1">
                  <h3 className="font-semibold text-gray-900">Want to list your show?</h3>
                  <p className="text-gray-600">Contact us to add your cat show to our calendar.</p>
                </div>
                <a href="/contact"><Button>Contact Us</Button></a>
              </div>
            </Card>
          </div>
        )}

        {/* Registries */}
        {activeTab === 'registries' && (
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">SA Cat Registries</h2>
            <div className="space-y-6">
              {REGISTRIES.map((reg) => (
                <Card key={reg.id} className="hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="w-20 h-20 bg-feline-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Award className="w-10 h-10 text-feline-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{reg.name}</h3>
                        <Badge className="bg-gray-100 text-gray-700">{reg.affiliation}</Badge>
                      </div>
                      <p className="text-gray-600 mb-4">{reg.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span><strong>Founded:</strong> {reg.founded}</span>
                        <span><strong>Members:</strong> {reg.members}</span>
                      </div>
                      <div className="flex flex-wrap gap-3 mt-4">
                        <a href={reg.website} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm"><ExternalLink className="w-4 h-4" /> Website</Button>
                        </a>
                        <a href={`mailto:${reg.email}`}>
                          <Button variant="outline" size="sm"><Mail className="w-4 h-4" /> Email</Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Breed Clubs */}
        {activeTab === 'clubs' && (
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Breed Clubs</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {CAT_CLUBS.map((club) => (
                <Card key={club.id} className="hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-feline-100 rounded-xl flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-feline-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">{club.name}</h3>
                  <Badge className="bg-feline-100 text-feline-700 mb-3">{club.breed}</Badge>
                  <p className="text-sm text-gray-600 mb-4">{club.members} members</p>
                  <a href={`mailto:${club.contact}`}>
                    <Button variant="outline" size="sm" className="w-full">
                      <Mail className="w-4 h-4" /> Contact Club
                    </Button>
                  </a>
                </Card>
              ))}
            </div>
            <Card className="mt-8 bg-feline-50 border-feline-200">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Users className="w-10 h-10 text-feline-500" />
                <div className="text-center sm:text-left flex-1">
                  <h3 className="font-semibold text-gray-900">Run a breed club?</h3>
                  <p className="text-gray-600">Get your club listed on ThePedigreeApp.</p>
                </div>
                <a href="/contact"><Button>Get Listed</Button></a>
              </div>
            </Card>
          </div>
        )}

        {/* Partners */}
        {activeTab === 'partners' && (
          <div>
            <h2 className="text-2xl font-display font-bold text-gray-900 mb-6">Our Partners</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PARTNERS.map((partner) => (
                <Card key={partner.id} className="hover:shadow-lg transition-shadow text-center">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Handshake className="w-8 h-8 text-feline-500" />
                  </div>
                  <Badge className="bg-feline-100 text-feline-700 mb-2">{partner.type}</Badge>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{partner.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{partner.description}</p>
                  <a href={partner.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm"><ExternalLink className="w-4 h-4" /> Visit Website</Button>
                  </a>
                </Card>
              ))}
            </div>
            <Card className="mt-8 bg-gradient-to-r from-feline-500 to-feline-600 text-white">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Handshake className="w-10 h-10 text-feline-100" />
                <div className="text-center sm:text-left flex-1">
                  <h3 className="font-semibold">Become a Partner</h3>
                  <p className="text-feline-100">Reach thousands of cat breeders and buyers across South Africa.</p>
                </div>
                <a href="/contact"><Button className="bg-white text-feline-600 hover:bg-feline-50">Partner With Us</Button></a>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommunityPage;
